import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import TelnyxAnimated from '../../assets/video/telnyx-animated.mp4';

import { login, refresh } from '@store/async';
import { AppDispatch, authActions, State } from '@store/index';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Stack } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { LoadingButton } from '@common/Button';
import { TelnyxLoader } from '@common/hooks/useLoader';
import { getServices } from '@store/async/service';
import { updateIntercom } from '@common/utils/intercom';

const Container = styled.div`
  display: grid;
  grid-template-columns: 3.5fr 7.5fr;
  background: black;
`;

const LoginWindowOutline = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  height: 100%;
  width: 100%;
  padding-left: 1.5rem;
  padding-top: 0.75rem;

  .box-outline {
    display: flex;
    align-items: center;
    justify-content: center;

    border: 4px dashed ${({ theme }) => theme.telnyx.blue};
    border-radius: 46px;
    height: calc(800px + 10rem);
    width: calc(700px + 5rem);
  }
`;

const Body = styled.div<{ active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;

  padding-top: 38vh;
  background: ${({ theme }) => theme.background.standard};
  width: calc(100% + 2.25rem);
  height: calc(100vh - 38vh);
  font-family: 'Space Grotesk';

  border-radius: 0 3.5rem 3.5rem 0;
  position: relative;
  z-index: 2;

  outline: 4px solid ${({ active, theme }) => (active ? theme.telnyx.green : 'black')};
  transition: outline 0.3s;

  @media (max-width: 1550px), (max-height: 900px) {
    border-radius: 0;
    outline: none;
    padding-top: 20vh;
    min-width: 500px;
    min-height: 600px;
    width: 100vw;
    height: 100vh;
  }
`;

const Animation = styled.div`
  position: relative;
  height: 100vh;
  overflow: hidden;
  z-index: 1;

  video {
    width: auto;
    height: 100%;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }

  @media (max-width: 440px), (max-height: 700px) {
    display: none;
  }
`;

export const Title = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: '#00E3AA';
  width: 400px;
  gap: 1rem;

  h1 {
    font-family: 'PPFormula';
    width: fit-content;
    font-size: 2.3ch;
    font-weight: 600;
    color: ${({ theme }) => theme.text.standard};
  }

  p {
    font-size: 1.6ch;
    font-family: 'Inter';
    max-width: 400px;
    margin-bottom: 0.75rem;
    color: ${({ theme }) => theme.navText};
  }
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 400px;
  margin-top: 1rem;
  gap: 1rem;

  & .MuiAlert-root {
    border: 0;
  }
`;

export const Badge = styled.div<{ color?: string; width?: string }>`
  position: absolute;
  top: 0;
  left: 0;

  margin-left: 1rem;
  margin-top: 1rem;

  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3px 8px;
  padding-bottom: 0;
  background-color: ${({ theme, color }) => color || theme.primary};
  color: ${({ color }) => (color === '#D3FFA6' || color === '#00E3AA' ? 'black' : 'white')};
  border-radius: 6px;
  transition: 0.3s ease-in-out;

  font-family: 'PPFormula';
`;

export const Pill = styled.div<{ color?: string; width?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 10px;
  border-radius: 6px;
  width: ${({ width }) => `${width}px` || '50px'};
  background-color: ${({ theme, color }) => color || theme.telnyx.green};
  transition: 0.3s ease-in-out;
`;

export const Login = () => {
  const dispatch = useDispatch<AppDispatch>();
  const vidRef: any = useRef();
  const titleRef: any = useRef();

  const navigate = useNavigate();
  const location = useLocation();

  const [activeLine, setActiveLine] = useState(false);

  const loginLoading = useSelector((state: State) => state.auth.loginLoading);
  const logoutLoading = useSelector((state: State) => state.auth.logoutLoading);

  const isLocalUser = useSelector((state: State) => state.auth.isLocalUser);
  const error = useSelector((state: State) => state.auth.error);
  const invalidPermission = useSelector((state: State) => state.auth.invalidPermission);
  const autoLogin = useSelector((state: State) => state.auth.autoLogin);
  const isLoggedIn = useSelector((state: State) => state.auth.isLoggedIn);
  const loginWindowOpen = useSelector((state: State) => state.auth.loginWindowOpen);

  const hasWorkspaceCheck = async () => {
    const services = await dispatch(getServices());
    const hasWorkspace = services?.payload?.length > 0;

    updateIntercom('flow-user-login', { has_workspace: hasWorkspace });

    window.Intercom?.('update', {
      has_workspace: hasWorkspace,
    });
  };

  const loginUser = async () => {
    const result = await dispatch(login());
    const from = location.state?.from || '/';

    if (result.type === 'auth/login/fulfilled' && !result?.payload?.error) {
      navigate(from, { replace: true });
      hasWorkspaceCheck();
    }
  };

  const refreshLogin = async () => {
    const result = await dispatch(refresh());

    if (result.type === 'auth/refresh/fulfilled' && !result?.payload?.error) {
      const from = location.state?.from || '/';
      navigate(from, { replace: true });
      hasWorkspaceCheck();
    }
  };

  useEffect(() => {
    onVideoLoop();
    if (!isLoggedIn && !loginLoading && !logoutLoading) refreshLogin();
  }, [location]);

  const onVideoLoop = (start = false) => {
    if (start && vidRef.current) vidRef.current.play();
    setTimeout(
      () => {
        setActiveLine(true);
        setTimeout(() => {
          setActiveLine(false);
        }, 2900);
      },
      start ? 3650 : 3850
    );
  };

  const titleWidth = titleRef?.current?.offsetWidth || '50';
  const colors = ['#00E3AA', '#3434EF', '#D3FFA6', '#FF7442'];

  const [colorIndex, setColorIndex] = useState(0);
  const [widthAdjustment, setWidthAdjustment] = useState(0);

  const widthAdjustmentRef = useRef(widthAdjustment);
  widthAdjustmentRef.current = widthAdjustment;

  const getRandomWidthAdjustment = () => {
    const adjustments = [-20, 0, 20, 60];
    let newAdjustment = adjustments[Math.floor(Math.random() * adjustments.length)];

    // Ensure the new adjustment is different from the previous one
    while (newAdjustment === widthAdjustmentRef.current) {
      newAdjustment = adjustments[Math.floor(Math.random() * adjustments.length)];
    }

    return newAdjustment;
  };

  const currentWidth = titleRef?.current ? titleWidth + widthAdjustment : '255';

  useEffect(() => {
    const intervalId = setInterval(() => {
      setColorIndex((prevIndex) => (prevIndex + 1) % colors.length);
      setWidthAdjustment(getRandomWidthAdjustment());
    }, 2000);

    return () => clearInterval(intervalId);
  }, []);

  return (
    <Container>
      <Body active={!loginWindowOpen ? activeLine : false}>
        <Title>
          <Stack spacing={1} alignItems='center' justifyContent='center'>
            <h1 ref={titleRef}>Flow</h1>
            <Pill color={colors[colorIndex]} width={currentWidth.toString() || '400'} />
          </Stack>
          <p>Deploy AI-empowered workflows.</p>
        </Title>
        <InputContainer>
          {error && (
            <Alert
              sx={{ width: 'calc(400px - 32px)' }}
              severity='error'
              onClose={!invalidPermission ? () => dispatch(authActions.setError(null)) : undefined}
            >
              {error}
            </Alert>
          )}
          {loginWindowOpen && (
            <Alert sx={{ width: 'calc(400px - 32px)' }} severity='info'>
              Login to your account in the new window to complete the login process.
            </Alert>
          )}
          <LoadingButton
            fullWidth
            disabled={(autoLogin && isLocalUser) || invalidPermission || loginLoading}
            onClick={loginUser}
            loading={loginLoading}
            minHeight='40px'
          >
            Login
          </LoadingButton>
        </InputContainer>
      </Body>
      {!loginWindowOpen && (
        <Animation>
          <video ref={vidRef} muted autoPlay playsInline onEnded={() => onVideoLoop(true)}>
            <source src={TelnyxAnimated} type='video/mp4' />
            Your browser does not support the video tag.
          </video>
        </Animation>
      )}
      {loginWindowOpen && (
        <LoginWindowOutline>
          <div className='box-outline'>
            <TelnyxLoader isDarkBackground={true} />
          </div>
        </LoginWindowOutline>
      )}
    </Container>
  );
};
